import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tournamentApi, eventApi, playerDashboardApi, playerApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
const fmtDayLong = (dateStr) => new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

const getRoundLabel = (matchesInRound) => {
  if (matchesInRound === 1) return 'Final';
  if (matchesInRound === 2) return 'Semi Finals';
  if (matchesInRound === 4) return 'Quarter Finals';
  if (matchesInRound === 8) return 'Round of 16';
  if (matchesInRound === 16) return 'Round of 32';
  return `Round of ${matchesInRound * 2}`;
};

const SeedBadge = ({ seed }) =>
  seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px' }}>[{seed}]</span> : null;

// ── Knockout Bracket ─────────────────────────────────────────────────────────

const parseScore = (score) => {
  if (!score) return null;
  try { return typeof score === 'string' ? JSON.parse(score) : score; } catch { return null; }
};

const extractGamePairs = (scoreData) => {
  if (!scoreData || scoreData.games === 'Walkover') return null;
  const pairs = [scoreData.game1, scoreData.game2, scoreData.game3]
    .filter(g => g && (parseInt(g.player1) > 0 || parseInt(g.player2) > 0))
    .map(g => [parseInt(g.player1) || 0, parseInt(g.player2) || 0]);
  return pairs.length > 0 ? pairs : null;
};

// BWF-style: winning game score is blue-tinted bold, losing score is greyed out
const ScoreGames = ({ pairs, playerIdx }) => {
  if (!pairs || pairs.length === 0) return null;
  return (
    <span style={{ marginLeft: 5, display: 'inline-flex', gap: 3, flexShrink: 0 }}>
      {pairs.map((pair, i) => {
        const mine = pair[playerIdx];
        const theirs = pair[1 - playerIdx];
        const won = mine > theirs;
        return (
          <span key={i} style={{
            fontSize: '0.68rem', fontWeight: won ? 700 : 400,
            color: won ? '#1d4ed8' : '#9ca3af',
            background: won ? '#eff6ff' : '#f3f4f6',
            border: `1px solid ${won ? '#93c5fd' : '#e5e7eb'}`,
            borderRadius: 3, padding: '1px 4px', minWidth: 18, textAlign: 'center',
          }}>
            {mine}
          </span>
        );
      })}
    </span>
  );
};

// Green circle dot shown next to the winner's name
const WinnerDot = () => (
  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block', marginRight: 5, flexShrink: 0 }} />
);

const KnockoutBracket = ({ matches }) => {
  const rounds = [...new Set(matches.map(m => m.round_number))].sort((a, b) => a - b);
  if (rounds.length === 0) return <div className="empty-state"><p>Draw not generated yet.</p></div>;

  const firstRoundCount = matches.filter(m => m.round_number === rounds[0]).length;
  const totalHeight = firstRoundCount * 120;

  return (
    <div style={{ display: 'flex', overflowX: 'auto', padding: '20px 10px', background: '#fcfcfc', borderRadius: '6px' }}>
      {rounds.map((roundNum, roundIdx) => {
        const roundMatches = matches.filter(m => m.round_number === roundNum).sort((a, b) => a.match_number - b.match_number);
        const slotHeight = roundMatches.length > 0 ? totalHeight / roundMatches.length : totalHeight;
        const label = getRoundLabel(roundMatches.filter(m => m.notes !== '3rd Place Playoff').length);
        return (
          <React.Fragment key={roundNum}>
            {roundIdx > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', width: '40px', flexShrink: 0 }}>
                <div style={{ visibility: 'hidden', padding: '8px', fontSize: '0.85rem', margin: '0 4px 8px' }}>·</div>
                <div style={{ position: 'relative', height: `${totalHeight}px` }}>
                  {roundMatches.map((m, idx) => (
                    <div key={`c-${m.id}`} style={{ position: 'absolute', top: `${idx * slotHeight}px`, height: `${slotHeight}px`, width: '100%' }}>
                      <div style={{ position: 'absolute', left: '50%', top: '25%', bottom: '25%', borderLeft: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '50%', right: '-4px', top: '50%', borderTop: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '-4px', width: 'calc(50% + 4px)', top: '25%', borderTop: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '-4px', width: 'calc(50% + 4px)', top: '75%', borderTop: '2px solid #adb5bd' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '220px', flexShrink: 0 }}>
              <div style={{ textAlign: 'center', padding: '8px', background: 'var(--primary-color)', color: 'white', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem', margin: '0 4px 8px' }}>
                {label}
              </div>
              <div style={{ position: 'relative', height: `${totalHeight}px` }}>
                {roundMatches.map((match, idx) => {
                  const scoreData = parseScore(match.score);
                  const gamePairs = extractGamePairs(scoreData);
                  const isWalkover = match.notes?.toLowerCase() === 'walkover';
                  const p1win = match.winner_id && match.winner_id === match.player1_id;
                  const p2win = match.winner_id && match.winner_id === match.player2_id;
                  return (
                    <div key={match.id} style={{ position: 'absolute', top: `${(idx + 0.5) * slotHeight}px`, transform: 'translateY(-50%)', left: '4px', right: '4px' }}>
                      <div style={{ border: match.winner_id ? '2px solid #28a745' : '1px solid #dee2e6', borderRadius: '6px', background: match.winner_id ? '#f8fff9' : 'white', overflow: 'hidden' }}>
                        {isWalkover && (
                          <div style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a', padding: '2px 8px', fontSize: '0.62rem', fontWeight: 700, color: '#92400e', letterSpacing: '0.05em' }}>
                            W/O · WALKOVER
                          </div>
                        )}
                        {/* Player 1 row */}
                        <div style={{ padding: '7px 10px', borderBottom: '1px solid #f1f1f1', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: p1win ? 700 : 'normal', color: p1win ? '#15803d' : match.winner_id ? '#9ca3af' : '#333', minWidth: 0 }}>
                            {p1win && <WinnerDot />}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {match.player1_name || (match.notes?.includes('BYE') ? 'BYE' : 'TBD')}
                            </span>
                            <SeedBadge seed={match.player1_seed} />
                          </span>
                          <ScoreGames pairs={gamePairs} playerIdx={0} />
                        </div>
                        {/* Player 2 row */}
                        <div style={{ padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: p2win ? 700 : 'normal', color: p2win ? '#15803d' : match.winner_id ? '#9ca3af' : '#333', minWidth: 0 }}>
                            {p2win && <WinnerDot />}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {match.player2_name || (match.notes?.includes('BYE') ? 'BYE' : 'TBD')}
                            </span>
                            <SeedBadge seed={match.player2_seed} />
                          </span>
                          <ScoreGames pairs={gamePairs} playerIdx={1} />
                        </div>
                        {match.match_start_time && match.match_end_time && (
                          <div style={{ padding: '3px 10px', borderTop: '1px solid #f0f0f0', background: '#f8fff9', fontSize: '0.65rem', color: '#6b7280', display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span>⏱</span>
                            <span>{Math.round((new Date(match.match_end_time) - new Date(match.match_start_time)) / 60000)} min</span>
                            <span style={{ color: '#d1d5db' }}>·</span>
                            <span>{new Date(match.match_start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}–{new Date(match.match_end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {match.status === 'in_progress' && match.match_start_time && (
                          <div style={{ padding: '3px 10px', borderTop: '1px solid #fde68a', background: '#fffbeb', fontSize: '0.65rem', color: '#d97706', fontWeight: 600 }}>
                            ⏱ In progress since {new Date(match.match_start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 2px' }}>
                        <span style={{ fontSize: '0.62rem', color: '#bbb' }}>
                          {match.notes === '3rd Place Playoff' ? '3rd Place' : `M${match.match_number}`}
                        </span>
                        <span style={{ fontSize: '0.62rem', color: '#bbb' }}>
                          {match.scheduled_time ? `${new Date(match.scheduled_time).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${fmtTime(match.scheduled_time)}` : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const PublicTournament = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const isPlayer = user?.role === 'player';

  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const matchesRef = useRef([]);
  const pollingRef = useRef(null);
  useEffect(() => { matchesRef.current = matches; }, [matches]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const registrationOpen = ['registration', 'draft'].includes(tournament?.status);

  // Player-specific: this player's entries in this tournament
  const [myEntries, setMyEntries] = useState([]);
  const [withdrawing, setWithdrawing] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDrawEventId, setSelectedDrawEventId] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [eventEntries, setEventEntries] = useState({});
  const [loadingEntries, setLoadingEntries] = useState({});

  useEffect(() => {
    const stopPolling = () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };

    const startPolling = () => {
      if (pollingRef.current) return; // already running
      pollingRef.current = setInterval(async () => {
        if (!matchesRef.current.some(m => m.is_live)) { stopPolling(); return; }
        try {
          const res = await tournamentApi.getMatches(id);
          setMatches(res.data || []);
        } catch { /* silent */ }
      }, 4000);
    };

    const load = async () => {
      try {
        const [tRes, mRes, eRes] = await Promise.all([
          tournamentApi.getById(id),
          tournamentApi.getMatches(id),
          eventApi.list(id).catch(() => ({ data: [] })),
        ]);
        setTournament(tRes.data);
        const loadedMatches = mRes.data || [];
        setMatches(loadedMatches);
        const evs = eRes.data || [];
        setEvents(evs);
        if (evs.length > 0) setSelectedDrawEventId(evs[0].id);
        if (loadedMatches.some(m => m.is_live)) startPolling();
        // Load entries for all events upfront (needed for accurate player counts and seeds)
        if (evs.length > 0) {
          const entryResults = await Promise.all(
            evs.map(ev => eventApi.listEntries(id, ev.id).catch(() => ({ data: [] })))
          );
          const entriesMap = {};
          evs.forEach((ev, i) => { entriesMap[ev.id] = entryResults[i].data || []; });
          setEventEntries(entriesMap);
        }
      } catch {
        setError('Tournament not found');
      } finally {
        setLoading(false);
      }
    };

    load();
    return stopPolling;
  }, [id]);

  // Fetch this player's entries for this tournament (player role only)
  const loadMyEntries = useCallback(async () => {
    if (!isAuthenticated || !isPlayer) return;
    try {
      const res = await playerDashboardApi.getMyEntries(id);
      setMyEntries(res.data.entries || []);
    } catch { /* silent */ }
  }, [id, isAuthenticated, isPlayer]);

  useEffect(() => { loadMyEntries(); }, [loadMyEntries]);

  const handleWithdraw = async (playerId, eventName) => {
    if (!window.confirm(`Withdraw from ${eventName}?`)) return;
    setWithdrawing(prev => ({ ...prev, [playerId]: true }));
    try {
      await playerApi.withdrawPlayer(id, playerId);
      toast.success(`Withdrawn from ${eventName}`);
      await loadMyEntries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to withdraw');
    } finally {
      setWithdrawing(prev => ({ ...prev, [playerId]: false }));
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const loadEntries = async (eventId) => {
    if (eventEntries[eventId] !== undefined) return;
    setLoadingEntries(prev => ({ ...prev, [eventId]: true }));
    try {
      const res = await eventApi.listEntries(id, eventId);
      setEventEntries(prev => ({ ...prev, [eventId]: res.data || [] }));
    } catch {
      setEventEntries(prev => ({ ...prev, [eventId]: [] }));
    } finally {
      setLoadingEntries(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // ── Derived data ────────────────────────────────────────────────────────────

  const hasEventMatches = matches.some(m => m.event_id != null && m.status !== 'deleted');

  const visibleMatches = useMemo(() =>
    matches.filter(m => m.status !== 'deleted' && (!hasEventMatches || m.event_id != null)),
    [matches, hasEventMatches]
  );

  const getEventMatches = (eventId) => visibleMatches.filter(m => m.event_id === eventId);

  const getEventPlayerCount = (eventId) => {
    const entries = eventEntries[eventId];
    if (entries && entries.length > 0) {
      // Count unique pairs for doubles; individual rows for singles
      const pairIds = new Set();
      let singlesCount = 0;
      entries.forEach(e => {
        if (e.pair_id) pairIds.add(e.pair_id);
        else singlesCount++;
      });
      return pairIds.size + singlesCount;
    }
    // Fall back to match data (BYE check per player slot, not per match)
    const players = new Set();
    getEventMatches(eventId).forEach(m => {
      if (m.player1_id && m.player1_name !== 'BYE') players.add(m.player1_id);
      if (m.player2_id && m.player2_name !== 'BYE') players.add(m.player2_id);
    });
    return players.size;
  };

  const getSeededPlayers = (eventId) => {
    const entries = eventEntries[eventId];
    if (entries && entries.length > 0) {
      // Use entries as source of truth for seeds; collapse doubles pairs
      const seeded = new Map();
      entries.filter(e => e.seed_number).forEach(e => {
        const key = e.pair_id || e.id;
        if (!seeded.has(key)) {
          const partner = e.pair_id ? entries.find(x => x.pair_id === e.pair_id && x.id !== e.id) : null;
          const name = partner ? `${e.player_name} / ${partner.player_name}` : e.player_name;
          seeded.set(key, { name, seed: e.seed_number });
        }
      });
      return [...seeded.values()].sort((a, b) => a.seed - b.seed);
    }
    // Fall back to match data
    const seeded = new Map();
    getEventMatches(eventId).forEach(m => {
      if (m.player1_id && m.player1_seed) seeded.set(m.player1_id, { name: m.player1_name, seed: parseInt(m.player1_seed) });
      if (m.player2_id && m.player2_seed) seeded.set(m.player2_id, { name: m.player2_name, seed: parseInt(m.player2_seed) });
    });
    return [...seeded.values()].sort((a, b) => a.seed - b.seed);
  };

  const getEventResults = (eventId) => {
    const evMatches = getEventMatches(eventId);
    const roundCounts = {};
    evMatches.filter(m => m.notes !== '3rd Place Playoff').forEach(m => {
      roundCounts[m.round_number] = (roundCounts[m.round_number] || 0) + 1;
    });
    const finalEntry = Object.entries(roundCounts).find(([, c]) => c === 1);
    const semiEntry = Object.entries(roundCounts).find(([, c]) => c === 2);
    const finalMatch = finalEntry ? evMatches.find(m => m.round_number === parseInt(finalEntry[0]) && m.notes !== '3rd Place Playoff') : null;
    const semiMatches = semiEntry ? evMatches.filter(m => m.round_number === parseInt(semiEntry[0])) : [];
    const winner = finalMatch?.winner_id
      ? { name: finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player1_name : finalMatch.player2_name, seed: finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player1_seed : finalMatch.player2_seed }
      : null;
    const runnerUp = finalMatch?.winner_id
      ? { name: finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player2_name : finalMatch.player1_name, seed: finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player2_seed : finalMatch.player1_seed }
      : null;
    const semiLosers = semiMatches.filter(m => m.winner_id).map(m => ({
      name: m.winner_id === m.player1_id ? m.player2_name : m.player1_name,
      seed: m.winner_id === m.player1_id ? m.player2_seed : m.player1_seed,
    }));
    return { winner, runnerUp, semiLosers, hasFinal: !!finalMatch, finalComplete: !!finalMatch?.winner_id };
  };

  const scheduleByDate = useMemo(() => {
    const byDate = {};
    visibleMatches
      .filter(m => m.scheduled_time && !m.notes?.includes('BYE'))
      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
      .forEach(m => {
        const date = new Date(m.scheduled_time).toISOString().split('T')[0];
        if (!byDate[date]) byDate[date] = [];
        byDate[date].push(m);
      });
    return byDate;
  }, [visibleMatches]);

  const scheduleDates = Object.keys(scheduleByDate).sort();

  const totalPlayers = useMemo(() => {
    const p = new Set();
    visibleMatches.forEach(m => {
      if (m.player1_id && !m.notes?.includes('BYE')) p.add(m.player1_id);
      if (m.player2_id && !m.notes?.includes('BYE')) p.add(m.player2_id);
    });
    return p.size;
  }, [visibleMatches]);

  const sportLabel = tournament?.sport_type
    ? tournament.sport_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : '';

  // ── Loading / Error ─────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (error || !tournament) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
      <p style={{ color: 'var(--text-secondary)' }}>{error || 'Tournament not found'}</p>
      <Link to="/tournaments" className="btn btn-primary">Browse Tournaments</Link>
    </div>
  );

  // ── Tab renderers ───────────────────────────────────────────────────────────

  const renderOverview = () => (
    <div>
      {/* Registration CTA */}
      {isPlayer && myEntries.length > 0 ? (
        // Player is registered — show their events and withdraw options
        <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #CCFBF1 100%)', border: '1px solid #86efac', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: '700', fontSize: '1rem', color: '#15803d', marginBottom: '0.5rem' }}>
            You are registered
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: myEntries.length < events.length && registrationOpen ? '0.75rem' : 0 }}>
            {myEntries.map(e => (
              <div key={e.player_entry_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                  {e.event_name}
                  <span style={{ color: '#9ca3af', marginLeft: 6, textTransform: 'capitalize' }}>· {e.entry_type}</span>
                </span>
                {e.event_status !== 'completed' && (
                  <button
                    onClick={() => handleWithdraw(e.player_entry_id, e.event_name)}
                    disabled={withdrawing[e.player_entry_id]}
                    style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: 'white', color: '#dc2626', cursor: 'pointer', fontWeight: 500 }}
                  >
                    {withdrawing[e.player_entry_id] ? 'Withdrawing…' : 'Withdraw'}
                  </button>
                )}
              </div>
            ))}
          </div>
          {registrationOpen && myEntries.length < events.length && (
            <Link to={`/t/${id}/register`} style={{ fontSize: '0.85rem', color: '#0D9488', fontWeight: 600, textDecoration: 'none' }}>
              + Register for another event
            </Link>
          )}
        </div>
      ) : registrationOpen ? (
        // Not registered yet (or not a player)
        <div style={{ background: 'linear-gradient(135deg, #CCFBF1 0%, #dbeafe 100%)', border: '1px solid #99F6E4', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#115E59', marginBottom: '0.2rem' }}>
              Registrations are open!
            </div>
            <div style={{ fontSize: '0.85rem', color: '#0F766E' }}>
              Secure your spot in {tournament.name}.
            </div>
          </div>
          <Link to={`/t/${id}/register`} className="btn btn-primary" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>
            Register Now →
          </Link>
        </div>
      ) : null}

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Sport', value: sportLabel || '—' },
          { label: 'Format', value: tournament.draw_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—' },
          { label: 'Events', value: events.length || (visibleMatches.length > 0 ? 1 : '—') },
          { label: 'Players', value: totalPlayers || '—' },
          { label: 'Start', value: tournament.start_date ? fmtDate(tournament.start_date) : '—' },
          { label: 'End', value: tournament.end_date ? fmtDate(tournament.end_date) : '—' },
        ].map(({ label, value }) => (
          <div key={label} className="card" style={{ padding: '1rem', textAlign: 'center', margin: 0 }}>
            <div style={{ fontSize: '1.15rem', fontWeight: '700', color: 'var(--primary-color)' }}>{value}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Events summary */}
      {events.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.06em' }}>Events</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {events.map(ev => (
              <div key={ev.id} style={{ padding: '0.4rem 0.75rem', background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 999, fontSize: '0.85rem', color: '#115E59', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontWeight: 600 }}>{ev.name}</span>
                {ev.event_type && <span style={{ fontSize: '0.68rem', background: '#99F6E4', borderRadius: 999, padding: '1px 5px' }}>{ev.event_type}</span>}
                {ev.age_category && <span style={{ fontSize: '0.68rem', background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '1px 5px' }}>{ev.age_category}</span>}
                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>({getEventPlayerCount(ev.id)} players)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Venue / details */}
      {(tournament.venue || tournament.organizer_name) && (
        <div className="card">
          <h4 style={{ margin: '0 0 0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.06em' }}>Details</h4>
          {tournament.venue && (
            <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              <span style={{ color: '#6b7280', marginRight: '0.5rem' }}>📍 Venue:</span>
              <span style={{ fontWeight: '600' }}>{tournament.venue}</span>
            </div>
          )}
          {tournament.organizer_name && (
            <div style={{ fontSize: '0.9rem' }}>
              <span style={{ color: '#6b7280', marginRight: '0.5rem' }}>🏢 Organizer:</span>
              <span style={{ fontWeight: '600' }}>{tournament.organizer_name}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderEvents = () => {
    if (events.length === 0) return (
      <div className="card"><div className="empty-state"><p>No events created yet.</p></div></div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {events.map(ev => {
          const isExpanded = expandedEventId === ev.id;
          const entries = eventEntries[ev.id];
          const playerCount = getEventPlayerCount(ev.id);
          const evMatches = getEventMatches(ev.id);
          const matchCount = evMatches.filter(m => !m.notes?.includes('BYE')).length;

          // Fallback player list from match data
          const playersFromMatches = (() => {
            const map = new Map();
            evMatches.forEach(m => {
              if (m.player1_id && !m.notes?.includes('BYE')) map.set(m.player1_id, { name: m.player1_name, seed: m.player1_seed });
              if (m.player2_id && !m.notes?.includes('BYE')) map.set(m.player2_id, { name: m.player2_name, seed: m.player2_seed });
            });
            return [...map.values()].sort((a, b) => (a.seed || 999) - (b.seed || 999) || (a.name || '').localeCompare(b.name || ''));
          })();

          const displayPlayers = (entries && entries.length > 0)
            ? entries.sort((a, b) => (a.seed_number || 999) - (b.seed_number || 999) || (a.player_name || '').localeCompare(b.player_name || ''))
            : null;

          return (
            <div key={ev.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <button
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                onClick={() => {
                  if (isExpanded) { setExpandedEventId(null); }
                  else { setExpandedEventId(ev.id); loadEntries(ev.id); }
                }}
              >
                <span style={{ fontWeight: '700', fontSize: '1rem', flex: 1 }}>{ev.name}</span>
                {ev.event_type && <span style={{ fontSize: '0.72rem', padding: '2px 8px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>{ev.event_type}</span>}
                {ev.age_category && <span style={{ fontSize: '0.72rem', padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 999 }}>{ev.age_category}</span>}
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{playerCount} players · {matchCount} matches</span>
                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{isExpanded ? '▲' : '▼'}</span>
              </button>

              {isExpanded && (
                <div style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.25rem' }}>
                  {loadingEntries[ev.id] ? (
                    <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading players…</p>
                  ) : displayPlayers ? (
                    <div className="table-container">
                      <table className="table" style={{ fontSize: '0.875rem' }}>
                        <thead><tr><th style={{ width: '36px' }}>#</th><th>Player</th><th style={{ width: '56px', textAlign: 'center' }}>Seed</th></tr></thead>
                        <tbody>
                          {displayPlayers.map((p, i) => (
                            <tr key={p.id || i}>
                              <td style={{ color: '#9ca3af' }}>{i + 1}</td>
                              <td style={{ fontWeight: p.seed_number ? '600' : 'normal' }}>
                                {p.profile_id
                                  ? <Link to={`/profiles/${p.profile_id}`} style={{ color: '#0D9488', textDecoration: 'none' }}>{p.player_name || p.display_name}</Link>
                                  : (p.player_name || p.display_name)
                                }
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {p.seed_number ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.82rem' }}>[{p.seed_number}]</span> : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : playersFromMatches.length > 0 ? (
                    <div className="table-container">
                      <table className="table" style={{ fontSize: '0.875rem' }}>
                        <thead><tr><th style={{ width: '36px' }}>#</th><th>Player</th><th style={{ width: '56px', textAlign: 'center' }}>Seed</th></tr></thead>
                        <tbody>
                          {playersFromMatches.map((p, i) => (
                            <tr key={i}>
                              <td style={{ color: '#9ca3af' }}>{i + 1}</td>
                              <td style={{ fontWeight: p.seed ? '600' : 'normal' }}>
                                {p.profile_id
                                  ? <Link to={`/profiles/${p.profile_id}`} style={{ color: '#0D9488', textDecoration: 'none' }}>{p.name}</Link>
                                  : p.name
                                }
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {p.seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.82rem' }}>[{p.seed}]</span> : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>No player data available yet.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDraw = () => {
    if (!hasEventMatches) {
      return (
        <div className="card">
          <h4 style={{ marginBottom: '1rem' }}>Tournament Draw</h4>
          {visibleMatches.length === 0
            ? <div className="empty-state"><p>Draw not generated yet.</p></div>
            : <KnockoutBracket matches={visibleMatches} />}
        </div>
      );
    }
    return (
      <div>
        {events.length > 1 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {events.map(ev => (
              <button
                key={ev.id}
                className={`btn btn-sm ${selectedDrawEventId === ev.id ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setSelectedDrawEventId(ev.id)}
              >
                {ev.name}{ev.age_category ? ` (${ev.age_category})` : ''}
              </button>
            ))}
          </div>
        )}
        {selectedDrawEventId && (() => {
          const ev = events.find(e => e.id === selectedDrawEventId);
          const evMatches = getEventMatches(selectedDrawEventId);
          return (
            <div className="card">
              {ev && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0 }}>{ev.name}</h4>
                  {ev.event_type && <span style={{ fontSize: '0.72rem', padding: '2px 8px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>{ev.event_type}</span>}
                  {ev.age_category && <span style={{ fontSize: '0.72rem', padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 999 }}>{ev.age_category}</span>}
                </div>
              )}
              {evMatches.length === 0
                ? <div className="empty-state"><p>Draw not generated for this event yet.</p></div>
                : <KnockoutBracket matches={evMatches} />}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderSchedule = () => {
    if (scheduleDates.length === 0) return (
      <div className="card"><div className="empty-state"><p>Schedule not generated yet. Check back soon.</p></div></div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {scheduleDates.map(date => (
          <div key={date} className="card" style={{ padding: '0.75rem 1rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '0.5rem' }}>
              {fmtDayLong(date)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {scheduleByDate[date].map((match, idx) => {
                const ev = events.find(e => e.id === match.event_id);
                const done = !!match.winner_id;

                // Parse score — prefer live score if match is in progress
                let scoreText = null;
                if (match.is_live && match.live_score) {
                  try {
                    const ls = typeof match.live_score === 'string' ? JSON.parse(match.live_score) : match.live_score;
                    const cp = ls.currentPoints || { p1: 0, p2: 0 };

                    const games = ls.games || [];
                    const pastGames = games.map(g => `${g.points.p1}-${g.points.p2}`).join(' ');
                    scoreText = `${cp.p1}-${cp.p2}${pastGames ? ` (${pastGames})` : ''}`;
                  } catch { /* ignore */ }
                } else if (match.score) {
                  try {
                    const sd = typeof match.score === 'string' ? JSON.parse(match.score) : match.score;
                    if (sd.games === 'Walkover') {
                      scoreText = 'W/O';
                    } else {
                      const pts = match.points_per_set || 21;
                      const cap = pts === 15 ? 21 : 30;
                      const pairs = [
                        [parseInt(sd.game1?.player1) || 0, parseInt(sd.game1?.player2) || 0],
                        [parseInt(sd.game2?.player1) || 0, parseInt(sd.game2?.player2) || 0],
                        [parseInt(sd.game3?.player1) || 0, parseInt(sd.game3?.player2) || 0],
                      ].filter(([a, b]) => !(a === 0 && b === 0));
                      if (pairs.length > 0) {
                        scoreText = pairs.map(([a, b]) => {
                          const gp = Math.max(a, b) === cap && Math.abs(a - b) === 1;
                          return `${a}-${b}${gp ? '⚡' : ''}`;
                        }).join('  ');
                      }
                    }
                  } catch { /* ignore */ }
                }

                const p1win = match.winner_id === match.player1_id;
                const p2win = match.winner_id === match.player2_id;

                return (
                  <div
                    key={match.id}
                    style={{
                      padding: '0.45rem 0',
                      borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none',
                    }}
                  >
                    {/* Meta row: time · court · event */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', minWidth: '44px' }}>
                        {fmtTime(match.scheduled_time)}
                      </span>
                      {match.court_number && (
                        <>
                          <span style={{ color: '#d1d5db', fontSize: '0.65rem' }}>·</span>
                          <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>Court {match.court_number}</span>
                        </>
                      )}
                      {ev && (
                        <>
                          <span style={{ color: '#d1d5db', fontSize: '0.65rem' }}>·</span>
                          <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#CCFBF1', color: '#115E59', borderRadius: 999, whiteSpace: 'nowrap' }}>
                            {ev.name}
                          </span>
                        </>
                      )}
                      {match.is_live && (
                        <>
                          <span style={{ color: '#d1d5db', fontSize: '0.65rem' }}>·</span>
                          <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#dc2626', whiteSpace: 'nowrap' }}>
                            🔴 LIVE
                          </span>
                        </>
                      )}
                    </div>

                    {/* Match row: player1 — score/vs — player2 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '44px' }}>
                      <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: p1win ? '700' : '500', color: p1win ? '#16a34a' : done ? '#9ca3af' : '#111', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.player1_name || 'TBD'}
                        {match.player1_seed ? <span style={{ fontSize: '0.62rem', color: '#92400e', marginLeft: '3px' }}>[{match.player1_seed}]</span> : ''}
                      </span>

                      <span style={{ flexShrink: 0, minWidth: '70px', textAlign: 'center', fontSize: '0.75rem', fontWeight: scoreText ? '600' : '400', color: scoreText ? '#374151' : '#c4c9d4', letterSpacing: scoreText ? '0.02em' : 0 }}>
                        {scoreText || '—'}
                      </span>

                      <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: p2win ? '700' : '500', color: p2win ? '#16a34a' : done ? '#9ca3af' : '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.player2_name || 'TBD'}
                        {match.player2_seed ? <span style={{ fontSize: '0.62rem', color: '#92400e', marginLeft: '3px' }}>[{match.player2_seed}]</span> : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSeeded = () => {
    const eventsWithSeeds = events.filter(ev => getSeededPlayers(ev.id).length > 0);
    if (events.length === 0 && visibleMatches.length === 0) return (
      <div className="card"><div className="empty-state"><p>Draw not generated yet.</p></div></div>
    );
    if (eventsWithSeeds.length === 0) return (
      <div className="card"><div className="empty-state"><p>No seeded players assigned yet.</p></div></div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {eventsWithSeeds.map(ev => (
          <div key={ev.id} className="card" style={{ padding: '0.75rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{ev.name}</span>
              {ev.event_type && <span style={{ fontSize: '0.65rem', padding: '1px 6px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>{ev.event_type}</span>}
              {ev.age_category && <span style={{ fontSize: '0.65rem', padding: '1px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 999 }}>{ev.age_category}</span>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
              {getSeededPlayers(ev.id).map(p => (
                <span key={p.seed} style={{ fontSize: '0.8rem', padding: '2px 9px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 999, whiteSpace: 'nowrap' }}>
                  <span style={{ fontWeight: 700, color: '#92400e' }}>[{p.seed}]</span> {p.name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderWinners = () => {
    const eventsWithDraw = events.filter(ev => getEventResults(ev.id).hasFinal);
    if (events.length === 0) return (
      <div className="card"><div className="empty-state"><p>No events found.</p></div></div>
    );
    if (eventsWithDraw.length === 0) return (
      <div className="card"><div className="empty-state"><p>Draws not generated yet — results will appear here once matches complete.</p></div></div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {eventsWithDraw.map(ev => {
          const { winner, runnerUp, semiLosers, finalComplete } = getEventResults(ev.id);
          return (
            <div key={ev.id} className="card" style={{ padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{ev.name}</span>
                {ev.event_type && <span style={{ fontSize: '0.65rem', padding: '1px 6px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>{ev.event_type}</span>}
                {ev.age_category && <span style={{ fontSize: '0.65rem', padding: '1px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 999 }}>{ev.age_category}</span>}
              </div>
              {!finalComplete ? (
                <p style={{ color: '#9ca3af', fontSize: '0.82rem', margin: 0 }}>In progress — results will appear as matches complete.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                  {winner && (
                    <span style={{ padding: '3px 10px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 999, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      🏆 <strong>{winner.name}</strong><SeedBadge seed={winner.seed} />
                    </span>
                  )}
                  {runnerUp && (
                    <span style={{ padding: '3px 10px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 999, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      🥈 {runnerUp.name}<SeedBadge seed={runnerUp.seed} />
                    </span>
                  )}
                  {semiLosers.map((p, i) => (
                    <span key={i} style={{ padding: '3px 10px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 999, fontSize: '0.82rem', color: '#4b5563', whiteSpace: 'nowrap' }}>
                      🥉 {p.name}<SeedBadge seed={p.seed} />
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Live matches ─────────────────────────────────────────────────────────────

  const liveMatches = visibleMatches.filter(m => m.is_live);

  const renderLive = () => {
    if (liveMatches.length === 0) return (
      <div className="card">
        <div className="empty-state">
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📡</div>
          <p style={{ fontWeight: '600', marginBottom: '0.25rem' }}>No matches are currently live.</p>
          <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Live scores will appear here during match play.</p>
        </div>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {liveMatches.map(match => {
          const ev = events.find(e => e.id === match.event_id);
          let ls = null;
          try {
            ls = match.live_score
              ? (typeof match.live_score === 'string' ? JSON.parse(match.live_score) : match.live_score)
              : null;
          } catch {}
          const cp = ls?.currentPoints || { p1: 0, p2: 0 };
          const gw = ls?.gamesWon || { p1: 0, p2: 0 };
          const games = ls?.games || [];
          const bestOf = ls?.settings?.bestOf || 3;
          const servingPlayer = ls?.servingPlayer;
          const challenges = ls?.challenges || {};
          const activeChallenge = ls?.activeChallenge;
          const pausedAfterGame = ls?.pausedAfterGame;
          const matchOver = ls?.matchOver;
          const p1 = match.player1_name || 'Player 1';
          const p2 = match.player2_name || 'Player 2';

          // Show brief outcome banner for the most recent resolved challenge (within 8s)
          const challengeLog = ls?.challengeLog || [];
          const lastResolved = challengeLog.length > 0 ? challengeLog[challengeLog.length - 1] : null;
          const recentOutcome = lastResolved && (Date.now() - lastResolved.resolvedAt) < 8000 ? lastResolved : null;

          return (
            <div key={match.id} className="card" style={{ padding: '1rem 1.25rem', border: `2px solid ${matchOver ? '#22c55e' : '#fca5a5'}` }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                {matchOver
                  ? <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#16a34a', letterSpacing: '0.06em' }}>🏆 MATCH OVER</span>
                  : <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#dc2626', letterSpacing: '0.06em' }}>🔴 LIVE</span>
                }
                {ev && <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>{ev.name}</span>}
                {match.court_number && <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>Court {match.court_number}</span>}
                <span style={{ fontSize: '0.72rem', color: '#9ca3af', marginLeft: 'auto' }}>
                  {pausedAfterGame ? `Game ${pausedAfterGame} complete` : matchOver ? 'Awaiting confirmation' : `Game ${ls?.currentGame || 1} of ${bestOf}`}
                </span>
              </div>

              {/* Active challenge banner */}
              {activeChallenge && (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.4rem 0.75rem', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ fontSize: '0.85rem' }}>⚡</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400e' }}>
                    Challenge in progress — {activeChallenge.player === 'p1' ? p1 : p2}
                  </span>
                </div>
              )}

              {/* Recent challenge outcome banner (fades away after ~8s polling cycles) */}
              {!activeChallenge && recentOutcome && (
                <div style={{
                  background: recentOutcome.outcome === 'successful' ? '#dcfce7' : '#fee2e2',
                  border: `1px solid ${recentOutcome.outcome === 'successful' ? '#86efac' : '#fca5a5'}`,
                  borderRadius: 8, padding: '0.4rem 0.75rem', marginBottom: '0.6rem',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}>
                  <span style={{ fontSize: '0.85rem' }}>{recentOutcome.outcome === 'successful' ? '✅' : '❌'}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: recentOutcome.outcome === 'successful' ? '#15803d' : '#b91c1c' }}>
                    {recentOutcome.outcome === 'successful'
                      ? `Challenge successful — ${recentOutcome.player === 'p1' ? p1 : p2} wins the point`
                      : `Challenge unsuccessful — point stands`}
                  </span>
                </div>
              )}

              {/* Score board */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '0.5rem' }}>
                {/* Player 1 */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {servingPlayer === 'p1' && <span style={{ marginRight: '4px' }}>🏸</span>}
                    {p1}
                    {match.player1_seed ? <span style={{ fontSize: '0.62rem', color: '#92400e', marginLeft: '3px' }}>[{match.player1_seed}]</span> : ''}
                  </div>
                </div>

                {/* Live score */}
                <div style={{ textAlign: 'center', minWidth: 90 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.15rem' }}>
                    <span style={{ fontWeight: '900', fontSize: '2.25rem', color: '#134E4A', lineHeight: 1 }}>{cp.p1}</span>
                    <span style={{ fontWeight: '700', fontSize: '1.2rem', color: '#9ca3af' }}>–</span>
                    <span style={{ fontWeight: '900', fontSize: '2.25rem', color: '#134E4A', lineHeight: 1 }}>{cp.p2}</span>
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#6b7280', marginTop: '0.15rem' }}>
                    Games: {gw.p1} – {gw.p2}
                  </div>
                </div>

                {/* Player 2 */}
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p2}
                    {match.player2_seed ? <span style={{ fontSize: '0.62rem', color: '#92400e', marginLeft: '3px' }}>[{match.player2_seed}]</span> : ''}
                    {servingPlayer === 'p2' && <span style={{ marginLeft: '4px' }}>🏸</span>}
                  </div>
                </div>
              </div>

              {/* Completed games strip */}
              {games.length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.65rem', flexWrap: 'wrap' }}>
                  {games.map((g, i) => (
                    <span key={i} style={{ fontSize: '0.7rem', padding: '1px 8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 999, color: '#374151' }}>
                      G{g.gameNumber}: {g.points.p1}–{g.points.p2}
                    </span>
                  ))}
                </div>
              )}

              {/* Challenge counts */}
              {ls && (challenges.p1 !== undefined || challenges.p2 !== undefined) && (
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: '1px solid #f0f0f0' }}>
                  {[{ key: 'p1', name: p1 }, { key: 'p2', name: p2 }].map(({ key, name }) => {
                    const left = challenges[key] ?? 2;
                    return (
                      <span key={key} style={{
                        fontSize: '0.68rem', padding: '2px 8px', borderRadius: 999,
                        background: left > 0 ? '#fef3c7' : '#f3f4f6',
                        color: left > 0 ? '#92400e' : '#9ca3af',
                        border: `1px solid ${left > 0 ? '#fbbf24' : '#e5e7eb'}`,
                        fontWeight: 600,
                      }}>
                        ⚡ {name.split(' ')[0]}: {left === 0 ? 'no challenges left' : `${left} challenge${left === 1 ? '' : 's'}`}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Page layout ─────────────────────────────────────────────────────────────

  const TABS = [
    { key: 'live',     label: liveMatches.length > 0 ? `🔴 Live (${liveMatches.length})` : 'Live' },
    { key: 'overview', label: 'Overview' },
    { key: 'events',   label: `Events${events.length > 0 ? ` (${events.length})` : ''}` },
    { key: 'draw',     label: 'Draw' },
    { key: 'schedule', label: `Schedule${scheduleDates.length > 0 ? ` (${scheduleDates.length}d)` : ''}` },
    { key: 'seeded',   label: 'Seeded Players' },
    { key: 'winners',  label: 'Winners' },
  ];

  const tabContent = { live: renderLive, overview: renderOverview, events: renderEvents, draw: renderDraw, schedule: renderSchedule, seeded: renderSeeded, winners: renderWinners };

  const statusColors = {
    draft:        { bg: '#f3f4f6', color: '#6b7280' },
    registration: { bg: '#dbeafe', color: '#1d4ed8' },
    seeding:      { bg: '#fef3c7', color: '#b45309' },
    in_progress:  { bg: '#d1fae5', color: '#065f46' },
    completed:    { bg: '#d1fae5', color: '#065f46' },
  };
  const sc = statusColors[tournament.status] || { bg: '#f3f4f6', color: '#6b7280' };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>

      {/* ── Hero Header ───────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, #0D9488 100%)', color: 'white', padding: '2.5rem 1rem 2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.82rem', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {sportLabel}{tournament.draw_type ? ` · ${tournament.draw_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}` : ''}
          </p>
          <h1 style={{ margin: '0 0 0.6rem', fontSize: 'clamp(1.6rem, 4vw, 2.6rem)', fontWeight: '800', letterSpacing: '-0.01em' }}>
            {tournament.name}
          </h1>
          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', opacity: 0.85 }}>
            {tournament.venue && <span>📍 {tournament.venue}</span>}
            {tournament.venue && tournament.start_date && <span>  ·  </span>}
            {tournament.start_date && (
              <span>📅 {fmtDate(tournament.start_date)}{tournament.end_date && tournament.end_date !== tournament.start_date ? ` – ${fmtDate(tournament.end_date)}` : ''}</span>
            )}
          </p>
          <span style={{ display: 'inline-block', padding: '3px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: '700', background: sc.bg, color: sc.color, letterSpacing: '0.04em' }}>
            {tournament.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
          <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {registrationOpen && isPlayer && myEntries.length > 0 ? (
              <span style={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: '700', fontSize: '0.85rem', padding: '0.5rem 1.25rem', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)' }}>
                ✓ Registered
              </span>
            ) : registrationOpen ? (
              <Link
                to={`/t/${id}/register`}
                className="btn"
                style={{ background: 'white', color: 'var(--primary-color)', fontWeight: '800', fontSize: '0.9rem', padding: '0.5rem 1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              >
                ✍️ Register Now
              </Link>
            ) : null}
            <button
              className="btn btn-sm"
              style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)' }}
              onClick={handleCopyLink}
            >
              {copied ? '✓ Copied!' : '🔗 Share Link'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Tab Bar ───────────────────────────────────────────────────────── */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', overflowX: 'auto' }}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '0.9rem 1.1rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === key ? '3px solid var(--primary-color)' : '3px solid transparent',
                color: activeTab === key ? 'var(--primary-color)' : '#6b7280',
                fontWeight: activeTab === key ? '700' : '500',
                fontSize: '0.85rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '1.5rem 1rem' }}>
        {tabContent[activeTab]?.()}
      </div>

      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af', fontSize: '0.78rem' }}>
        Powered by Tournament Scheduling Software
      </div>
    </div>
  );
};

export default PublicTournament;
